
import React, { useEffect, useMemo, useReducer } from "react";
import PageContainer from "../../components/PageContainer";
import MainContent from "../../components/MainContent";
import PageTitle from "../../components/Common/PageTitle/PageTitle";
import TextInput from "../../components/Common/TextInput";
import { useAccounts } from "../../DataStore/AccountsContext";
import { useTag } from "../../DataStore/TagContext";
import { useTheme } from "../../DataStore/ThemeContext";
import Notification from "../../components/Common/Notification";
import { useRefreshTrans } from "../../DataStore/useRefreshTrans";
import ajaxAsset from "../../util/remote/ajaxAsset";
import VendorSelector from "../../components/Transaction/VendorSelector";
import SectionTitle from "../../components/Common/SectionTitle";
import AccountingHint from "../../components/Ledger/AccountingHint";

const initialState = {
  loading: false,
  originAccount: null,
  paymentAccount: null,
  amount: "",
  description: "",
  transactionRef: "",
  transactionDate: "",
  payee: null,
  tag: null,
  contra_type: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "tag":
      return { ...state, tag: action.value };
    case "payee":
      return { ...state, payee: action.value };
    case "RESET_PARTIAL":
      return { ...state, amount: "", description: "" };
    default:
      return { ...state, [action.type]: action.value };
  }
};

// 🔹 helper: consistent responsive columns (1 / 2 / 3 per row)
const Col = ({ children }) => (
  <div className="col-12 col-md-6 col-xl-4">{children}</div>
);

const NewContraPage = () => {
  const { openSettings, setNotify } = useTheme();
  const refreshBalances = useRefreshTrans();
  const [state, dispatch] = useReducer(reducer, initialState);

  const { getReceiptAccounts, assetAccountListing, getAssetAccounts } = useAccounts();
  const { getTagListing, tagListing } = useTag();

  useEffect(() => {
    getReceiptAccounts();
    getAssetAccounts();
    getTagListing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const accountOptions = useMemo(() => {
    const opts = [];
    if (Array.isArray(assetAccountListing)) {
      assetAccountListing.forEach((a) =>
        opts.push({
          label: `${a.account_number} ${a.account_name}`,
          value: a.account_id,
        })
      );
    }
    return opts;
  }, [assetAccountListing]);

  const tagOptions = useMemo(() => {
    const opts = [
      { label: "Create New Project...", value: "add" },
      { label: "- No project -", value: "0" },
    ];
    if (Array.isArray(tagListing)) {
      tagListing.forEach((t) => opts.push({ label: t.tag_name, value: t.tag_id }));
    }
    return opts;
  }, [tagListing]);

  const journalHint = useMemo(() => {
    const from = state.originAccount?.label || "Source Account";
    const to = state.paymentAccount?.label || "Destination Account";
    return `Contra journal: Debit ${to}, Credit ${from}. No income/expense is recorded.`;
  }, [state.originAccount, state.paymentAccount]);

  const onConfirm = async (event) => {
    event.preventDefault();

    if (Number(state.amount) <= 0) {
      setNotify(<Notification type="danger" message="Enter amount and try again" />);
      return;
    }

    if (!state.originAccount?.value || !state.paymentAccount?.value) {
      setNotify(
        <Notification
          type="danger"
          message="Select both Funds origin account and funds destination account"
        />
      );
      return;
    }

    if (!state.payee?.value) {
      setNotify(<Notification type="danger" message="Select payee - i.e. the funds beneficiary" />);
      return;
    }

    dispatch({ type: "loading", value: true });

    const payload= {
      trail_account: state.originAccount.value,
      target_account: state.paymentAccount.value,
      amount: state.amount,
      transaction_date: state.transactionDate,
      description: state.description,
      payee: state.payee.value,
      transaction_reference: state.transactionRef,
      tag: state.tag?.value || "0",
      opening_balance: state.contra_type === "opening_balance" ? "1" : "0",
    };

    const server_response = await ajaxAsset.addContra(payload);

    dispatch({ type: "loading", value: false });

    if (server_response.status === "OK") {
      dispatch({ type: "RESET_PARTIAL" });
      setNotify(<Notification type="success" message={server_response.message} />);
      refreshBalances();
    } else {
      setNotify(<Notification type="danger" message={server_response.message} />);
    }
  };

  return (
    <PageContainer>
      <MainContent>
        <PageTitle
          title="New Contra - Funds Transfer"
          description="Create a new asset contra - exchange funds between accounts: this is not an expense record."
          iconClass="fa fa-exchange-alt"
        />

        <div className="mb-3">
          <AccountingHint hint={journalHint} />
        </div>

        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            <form method="post" onSubmit={onConfirm}>
              {/* ---------- CONTRA ACCOUNTS ---------- */}
              <div className="row mb-2">
                <div className="col-12">
                  <SectionTitle
                    icon="fa fa-bank"
                    title="Contra Accounts"
                    note="Record transfer of funds between asset accounts."
                  />
                </div>
              </div>

              <div className="row" style={{ rowGap: 12 }}>
                <Col>
                  <TextInput
                    type="select"
                    label="Source Account"
                    placeholder="Select origin of the funds"
                    leftIcon="fa fa-university"
                    options={accountOptions}
                    onChange={(item) => dispatch({ type: "originAccount", value: item })}
                    leftComponent={
                      <i className="fa fa-sync link" onClick={() => getAssetAccounts(true)} />
                    }
                    loading={!assetAccountListing}
                    value={state.originAccount}
                    wrap
                  />
                </Col>

                <Col>
                  <TextInput
                    type="select"
                    label="Destination Account"
                    placeholder="Select destination of the funds"
                    leftIcon="fa fa-university"
                    options={accountOptions}
                    onChange={(item) => dispatch({ type: "paymentAccount", value: item })}
                    leftComponent={
                      <i className="fa fa-sync link" onClick={() => getAssetAccounts(true)} />
                    }
                    loading={!assetAccountListing}
                    value={state.paymentAccount}
                    wrap
                  />
                </Col>

                <Col>
                  <TextInput
                    type="number"
                    label="Amount"
                    placeholder="Amount"
                    leftIcon="fa fa-credit-card"
                    value={state.amount}
                    onChange={(e) => dispatch({ type: "amount", value: e.target.value })}
                    required
                    wrap
                  />
                </Col>
              </div>

              {/* ---------- CONTRA DETAILS ---------- */}
              <div className="row mt-3 mb-2">
                <div className="col-12">
                  <SectionTitle
                    icon="fa fa-info-circle"
                    title="Contra Details"
                    note="Provide details of the contra transaction."
                  />
                </div>
              </div>

              <div className="row" style={{ rowGap: 12 }}>
                <Col>
                  <TextInput
                    type="date"
                    label="Transaction Date"
                    placeholder="dd/mm/yyyy"
                    leftIcon="fa fa-calendar-alt"
                    value={state.transactionDate}
                    onChange={(e) => dispatch({ type: "transactionDate", value: e.target.value })}
                    required
                    wrap
                  />
                </Col>

                <Col>
                  <TextInput
                    type="text"
                    label="Transaction Ref"
                    placeholder="Transaction Ref"
                    leftIcon="fa fa-hashtag"
                    value={state.transactionRef}
                    onChange={(e) => dispatch({ type: "transactionRef", value: e.target.value })}
                    required
                    wrap
                  />
                </Col>

                <Col>
                 <VendorSelector
                      onChange={(item) => {
                        if (item?.value === "add") {
                          openSettings("Create New Vendor", "NewVendor");
                          return;
                        }
                        dispatch({ type: "payee", value: item });
                      }}
                      placeholder="Select transactor"
                      value={state.payee}
                      wrap
                    />
                </Col>
              </div>

              {/* Description spans full width on xl+ */}
              <div className="row" style={{ rowGap: 12 }}>
                <div className="col-md-12">
                  <TextInput
                    type="textarea"
                    label="Funds Description"
                    placeholder="What is the reason for the transfer. E.g Imprest for November"
                    leftIcon="fa fa-list"
                    value={state.description}
                    onChange={(e) => dispatch({ type: "description", value: e.target.value })}
                    required
                    wrap
                  />
                </div>
               

              </div>
            <div className="row" style={{ rowGap: 12 }}>
                <div className="col-12">
                  {/* ---------- CLASSIFICATION ---------- */}
                  <SectionTitle
                    icon="fa-tag"
                    title="Classification"
                    note="Optional: attach a project to help reporting."
                  />
                  <div className="row" style={{ rowGap: 12 }}>
                    <div className="col-md-6">
                  <TextInput
                    type="select"
                    label="Tag Transaction"
                    placeholder={state.tag?.label || "Transaction is not tagged..."}
                    leftIcon="fa fa-tag"
                    options={tagOptions}
                    onChange={(item) => {
                      if (item?.value === "add") {
                        openSettings("Create New Project", "NewTag", null, "Create Project");
                        return;
                      }
                      dispatch({ type: "tag", value: item });
                    }}
                    leftComponent={<i className="fa fa-tag link" onClick={() => getTagListing(true)} />}
                    loading={!tagListing}
                    value={state.tag}
                    wrap
                  />
                  </div>
                  <div className="col-md-6">
                     <TextInput
                      type="select"
                      label="Contra Type"
                      placeholder={"Select contra type..."}
                      leftIcon="fa fa-exchange-alt"
                      options={[
                        { label: "Normal Transaction", value: "normal" },
                        { label: "Opening Balance", value: "opening_balance" },
                      ]}
                      onChange={(item) => {
                        dispatch({ type: "contra_type", value: item.value });
                      }}
                      value={state.contra_type ? state.contra_type : "normal"}
                      wrap
                    />
                </div>
              </div>
              </div>
              </div>

              {/* Actions */}
              <div className="d-flex flex-column flex-md-row justify-content-end mt-3" style={{ gap: 10 }}>
                <button type="submit" className="btn btn-primary btn-lg" disabled={state.loading}>
                  {state.loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-check-circle mr-2" />
                      Save Contra
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className="btn btn-light btn-lg"
                  onClick={() => dispatch({ type: "RESET_PARTIAL" })}
                  disabled={state.loading}
                >
                  <i className="fa fa-undo mr-2" />
                  Clear Amount & Note
                </button>
              </div>

              <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                Tip: Contra transfers should use asset accounts (Cash at Bank / Cash on Hand).
              </div>
            </form>
          </div>
        </div>
      </MainContent>
    </PageContainer>
  );
};

export default NewContraPage;
